import type { RequestOptions } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { metaFetcher, payloadCmsFetcher } from '@/libs/fetcher/presets'
import { CmsCoinDisclosure } from '@/types/cms'
import { CoinMeta } from '@/types/coin'

export const fetchCoinBySlug = (slug: string, requestOptions?: RequestOptions, silent404 = false) =>
  metaFetcher<CoinMeta>(`/tokens/${slug}`, requestOptions, {
    customData: { silent404 },
  })

export const coinBySlugQueryOptions = (slug: string, silent404 = false) =>
  queryOptions({
    queryKey: ['coinBySlug', slug, silent404],
    queryFn: () => fetchCoinBySlug(slug, undefined, silent404),
  })

export const useCoinBySlug = (slug: string, silent404 = false) =>
  useSuspenseQuery(coinBySlugQueryOptions(slug, silent404))

export const fetchCoinLocaleDesc = (id: number, locale = 'en', silent404 = false) =>
  metaFetcher<string>(`/tokens/${id}/desc/${locale}`, undefined, {
    customData: { silent404 },
  })

export const coinLocaleDescQueryOptions = (id: number, locale = 'en', silent404 = false) =>
  queryOptions({
    queryKey: ['coinLocaleDesc', id, locale, silent404],
    queryFn: () => fetchCoinLocaleDesc(id, locale, silent404).catch(() => ''),
    // Return empty string if API returns null
    select: data => data || '',
  })

export const useCoinLocaleDesc = (id: number, locale = 'en', silent404 = false) =>
  useSuspenseQuery(coinLocaleDescQueryOptions(id, locale, silent404))

export const fetchCmsCoinDisclosure = (slug: string) =>
  payloadCmsFetcher<CmsCoinDisclosure>(`/coins/${slug}`, undefined, {
    customData: { enableDefaultNextjsServerFetchCache: true },
  })

export const cmsCoinDisclosureQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ['cmsCoinDisclosure', slug],
    queryFn: () => fetchCmsCoinDisclosure(slug),
  })

export const useCmsCoinDisclosure = (slug: string) => {
  return useSuspenseQuery(cmsCoinDisclosureQueryOptions(slug))
}
